import { useState } from 'react'
import { ChevronLeft, UserRound, Sun, Moon, Monitor, Settings, LogOut, RotateCcw } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { CONFIG } from '@/config'
import { useMobileHeader } from '@/contexts/MobileHeaderContext'
import { useTheme } from '@/contexts/ThemeContext'
import { useAuth } from '@/contexts/AuthContext'
import { BottomSheet } from '@/ui'
import { clsx } from 'clsx'

const themeOptions = [
  { value: 'light' as const, label: 'Light', icon: Sun },
  { value: 'dark' as const, label: 'Dark', icon: Moon },
  { value: 'system' as const, label: 'System', icon: Monitor },
]

const accountActions = (navigate: ReturnType<typeof useNavigate>, logout: () => void, close: () => void) => [
  {
    label: 'Account Settings',
    icon: Settings,
    onClick: () => { close(); navigate('/profile') },
    className: 'text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--surface-hover)]',
  },
  {
    label: 'Reset filters',
    icon: RotateCcw,
    onClick: () => { sessionStorage.clear(); close(); window.location.reload() },
    className: 'text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--surface-hover)]',
  },
  {
    label: 'Sign out',
    icon: LogOut,
    onClick: () => { logout(); navigate('/login', { replace: true }) },
    className: 'text-[var(--status-error-text)] hover:bg-[var(--status-error-bg)]',
  },
]

export default function MobileHeader() {
  const { config } = useMobileHeader()
  const { theme, setTheme } = useTheme()
  const { user, logout } = useAuth()
  const navigate = useNavigate()
  const [accountOpen, setAccountOpen] = useState(false)

  return (
    <>
      <div className="md:hidden bg-[var(--surface-card)] border-b border-[var(--border-default)] px-4 flex items-center justify-between h-13 very-short:h-10 shrink-0">
        {/* Left — back button on nested pages, logo on top-level */}
        {config.showBack ? (
          <button
            type="button"
            onClick={() => navigate(-1)}
            aria-label={config.title ? `Back to previous page (${config.title})` : 'Back to previous page'}
            className="flex items-center gap-1.5 -ml-1 p-1.5 rounded-lg text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--surface-hover)] transition-colors"
          >
            <ChevronLeft className="h-5 w-5" />
            {config.title && (
              <span className="text-sm font-semibold text-[var(--text-primary)] truncate max-w-[200px]">
                {config.title}
              </span>
            )}
          </button>
        ) : (
          <img
            src={CONFIG.ASSETS.logo}
            alt={CONFIG.CLIENT.name}
            className="h-7 very-short:h-6 object-contain"
          />
        )}

        {/* Right — avatar opens account sheet */}
        <button
          type="button"
          onClick={() => setAccountOpen(true)}
          aria-label="Open account menu"
          className="flex h-7 w-7 items-center justify-center rounded-full border border-[var(--border-strong)] hover:bg-[var(--surface-hover)] transition-colors"
        >
          <UserRound className="h-3.5 w-3.5 text-[var(--text-secondary)]" />
        </button>
      </div>

      {/* Account bottom sheet */}
      <BottomSheet isOpen={accountOpen} onClose={() => setAccountOpen(false)}>
        {/* User info */}
        <div className="px-5 pt-4 pb-3 border-b border-[var(--border-default)]">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-[var(--border-strong)] bg-[var(--surface-hover)]">
              <UserRound className="h-4 w-4 text-[var(--text-secondary)]" />
            </div>
            <div>
              <p className="text-sm font-semibold text-[var(--text-primary)]">{user!.name}</p>
              <p className="text-xs text-[var(--text-secondary)]">{user!.contactNo}</p>
            </div>
          </div>
        </div>

        {/* Theme picker */}
        <div className="px-5 py-3 border-b border-[var(--border-default)]">
          <p className="text-[10px] font-semibold uppercase tracking-widest text-[var(--text-muted)] mb-2">Theme</p>
          <div className="flex gap-2">
            {themeOptions.map(({ value, label, icon: Icon }) => (
              <button
                key={value}
                onClick={() => setTheme(value)}
                className={clsx(
                  'flex-1 flex flex-col items-center gap-1 py-2.5 rounded-lg border text-xs font-medium transition-colors',
                  theme === value
                    ? 'border-[var(--brand-primary)] bg-[var(--brand-primary-subtle)] text-[var(--brand-primary)]'
                    : 'border-[var(--border-default)] text-[var(--text-secondary)] hover:bg-[var(--surface-hover)]'
                )}
              >
                <Icon className="h-4 w-4" />
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* Actions */}
        <div className="px-5 py-2 pb-4 space-y-0.5">
          {accountActions(navigate, logout, () => setAccountOpen(false)).map(({ label, icon: Icon, onClick, className }) => (
            <button
              key={label}
              onClick={onClick}
              className={clsx('flex w-full items-center gap-3 px-2 py-2.5 rounded-lg text-sm transition-colors', className)}
            >
              <Icon className="h-4 w-4" />
              {label}
            </button>
          ))}
        </div>
      </BottomSheet>
    </>
  )
}
